/**
 * Real bips copy and data for every landing variant. Variants may rephrase headlines to fit
 * their composition, but facts (numbers, venues, guardrails, disclosures) come from here.
 */
import { defaultChain } from "@v4ult/shared/chains";

const stocks = defaultChain.assets.filter((a) => a.kind === "stock");
/** Robinhood Stock Tokens and ETFs with a routable Uniswap pool on Robinhood Chain (generated catalog). */
export const stockCount = stocks.length;

export const brand = {
  name: "bips",
  tagline: "Your vault. Your rules. On autopilot.",
  oneLiner:
    "Personal vaults for Robinhood Stock Tokens, crypto and USDG on Robinhood Chain, kept on target by an AI agent that rebalances through Uniswap v4 aggregator hooks.",
  bio: "Your vault. Your rules. Stock Tokens, crypto & USDG on Robinhood Chain, rebalanced by an AI agent via Uniswap v4 aggregator hooks.",
  x: {
    handle: "@useBips",
    href: "https://x.com/useBips",
  },
  cta: {
    primary: { label: "Build your vault", href: "/app/new" },
    secondary: { label: "Open the app", href: "/app" },
  },
} as const;

/** Product facts that are true today. Never invent TVL, users or returns. */
export const facts = [
  { value: String(stockCount), label: "Stocks & ETFs with Uniswap pools" },
  { value: "2", label: "Routes quoted for every swap" },
  { value: "12", label: "Assets per vault, max" },
  { value: "0", label: "Ways the agent can withdraw" },
] as const;

export const networks = [
  { name: "Robinhood Chain", note: "Robinhood Stock Tokens & ETFs, USDG, ETH, cbBTC and more" },
] as const;

/** Example allocation used by heroes and demos. Always labelled as an example in UI. */
export const exampleVault = {
  name: "Frontier mix",
  totalUsd: 10_000,
  bandBps: 500,
  targets: [
    { symbol: "SPCX", name: "SpaceX", kind: "stock", weightBps: 2500 },
    { symbol: "NVDA", name: "NVIDIA", kind: "stock", weightBps: 2000 },
    { symbol: "AAPL", name: "Apple", kind: "stock", weightBps: 1500 },
    { symbol: "USDG", name: "Global Dollar", kind: "stable", weightBps: 3000 },
    { symbol: "WETH", name: "Wrapped Ether", kind: "crypto", weightBps: 1000 },
  ],
} as const;

export const steps = [
  {
    id: "pick",
    title: "Pick your mix",
    body: `Choose from ${stockCount} Robinhood Stock Tokens and ETFs, plus crypto and USDG. Set the weights yourself or start from a preset.`,
  },
  {
    id: "rules",
    title: "Set your rules",
    body: "Your drift band, how often to check, max slippage, a cost cap per swap and a minimum swap size. The agent can only act inside them.",
  },
  {
    id: "autopilot",
    title: "Go autopilot",
    body: "The agent values your vault on schedule. When an asset leaves its band it plans the fewest direct swaps back to target, and logs every decision, including when it holds.",
  },
] as const;

export const rules = [
  { label: "Drift band", value: "±3 / 5 / 7.5 / 10 pts", note: "Act only when an asset leaves its band" },
  { label: "Check every", value: "15 min to daily", note: "How often the agent values the vault" },
  { label: "Max slippage", value: "0.3–1%", note: "Enforced on-chain as the minimum you receive" },
  { label: "Cost cap", value: "0.3–1% per swap", note: "Skip a swap when gas + fees + impact are too high" },
  { label: "Min swap", value: "$5–$500", note: "Small corrections wait until they're worth the gas" },
] as const;

export const agentLoop = [
  { id: "value", title: "Value", body: "Reads balances and prices every asset in your vault." },
  { id: "drift", title: "Measure drift", body: "Compares each weight with its target and your band." },
  {
    id: "plan",
    title: "Plan",
    body: "Pairs overweight with underweight assets: the fewest direct swaps, never a double hop through a stablecoin.",
  },
  {
    id: "quote",
    title: "Quote twice",
    body: "Asks Uniswap's Guidestar routing (aggregator hooks) and standard routing at once, keeps the higher guaranteed output.",
  },
  { id: "check", title: "Check costs", body: "Skips any swap whose gas, fees and impact exceed your cap." },
  {
    id: "swap",
    title: "Swap & verify",
    body: "Executes through your vault contract, which checks balances afterwards.",
  },
] as const;

export const venues = [
  "Uniswap v4",
  "Curve",
  "Fluid",
  "PancakeSwap v3",
  "Slipstream",
  "LitePSM",
  "v2/v3 forks",
] as const;

export const guardrails = {
  can: [
    "Swap between assets already in your allocation",
    "Use only routers allowlisted by the bips factory",
    "Approve exactly the amount it sells, reset to zero afterwards",
    "Act until the expiry you set, on the check schedule you chose",
  ],
  cant: [
    "Withdraw, transfer, or send anything to another address",
    "Accept less than the quoted minimum; balances are checked after every swap",
    "Change your assets or target weights",
    "Keep trading after you pause, revoke, or let its authorisation lapse",
  ],
  owner: [
    "Self-custodial: only you can withdraw from your vault",
    "Pause or revoke the agent in one transaction",
    "Withdraw any token, any time",
  ],
} as const;

/** The closing call to action: create a real vault on Robinhood Chain, with your own contract. */
export const firstVault = {
  title: "Create your first",
  accent: "vault",
  body: "Pick your stocks and crypto, set your weights and rules, and create your vault on Robinhood Chain with a USDG deposit. The agent keeps it on target from there; withdraw any time.",
} as const;

export const faq = [
  {
    q: "Who holds my assets?",
    a: "You do. Your assets stay in your personal vault; the bips agent can only swap inside it, never withdraw.",
  },
  {
    q: "What are aggregator hooks?",
    a: "Uniswap v4 hooks that give outside liquidity (Curve, Fluid, PancakeSwap and more) a v4 interface, so one swap can reach it and settle in a single transaction.",
  },
  {
    q: "Why Stock Tokens?",
    a: "Robinhood Stock Tokens are ERC-20s on Robinhood Chain that track US stocks and ETFs, so a vault can hold SpaceX next to ETH and USDG and rebalance between them through Uniswap.",
  },
  {
    q: "Can I stop the agent?",
    a: "Yes. Pause or revoke it from your vault in one transaction, and withdraw any token at any time.",
  },
] as const;

export const disclosures =
  "Beta software. Smart contracts are unaudited. Robinhood Stock Tokens provide price exposure rather than legal ownership, carry issuer and market-hours risk, are not offered to US persons, and their availability depends on your jurisdiction. Nothing here is investment advice. bips is not affiliated with Robinhood or Uniswap Labs.";

export const nav = [
  { label: "How it works", href: "#how" },
  { label: "Agent", href: "#agent" },
  { label: "Guardrails", href: "#guardrails" },
  { label: "FAQ", href: "#faq" },
] as const;
